import { useEffect, useState } from "react";
import AccessManagementNav from "@/components/AccessManagementNav";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PAGE_PERMISSIONS } from "@/lib/permissions";
import { BILLING_PERIOD_LABELS, formatPackagePrice } from "@/lib/packages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Save, Plus, Pencil, Trash2, Package, Info, Banknote } from "lucide-react";

const emptyForm = {
  name: "",
  description: "",
  permissions: ["dashboard"],
  price: "0",
  currency: "TRY",
  billing_period: "monthly",
};

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/packages");
      setPackages(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (pkg) => {
    setEditing(pkg);
    setForm({
      name: pkg.name || "",
      description: pkg.description || "",
      permissions: pkg.permissions || [],
      price: String(pkg.price ?? 0),
      currency: pkg.currency || "TRY",
      billing_period: pkg.billing_period || "monthly",
    });
    setOpen(true);
  };

  const togglePerm = (key) => {
    setForm((prev) => {
      const has = prev.permissions.includes(key);
      const next = has ? prev.permissions.filter((k) => k !== key) : [...prev.permissions, key];
      return { ...prev, permissions: next.length ? next : ["dashboard"] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Paket adı gerekli"); return; }
    const price = parseFloat(form.price);
    if (Number.isNaN(price) || price < 0) { toast.error("Geçerli bir fiyat girin"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name,
        description: form.description,
        permissions: form.permissions,
        price,
        currency: form.currency,
        billing_period: form.billing_period,
      };
      if (editing) {
        await api.put(`/packages/${editing.id}`, body);
        toast.success("Paket güncellendi");
      } else {
        await api.post("/packages", body);
        toast.success("Paket oluşturuldu");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/packages/${deleteTarget.id}`);
      toast.success("Paket silindi");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="packages-page">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Abonelik Yönetimi</p>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">Paketler</h1>
          </div>
          <Button onClick={openCreate} className="h-11 rounded-xl bg-violet-600 px-6 font-semibold text-white hover:bg-violet-700">
            <Plus className="mr-2 h-4 w-4" /> Yeni Paket
          </Button>
        </header>

        <AccessManagementNav />

        <Card className="mb-6 rounded-2xl border-violet-200/60 bg-violet-50/50 p-4">
          <div className="flex gap-3">
            <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-violet-600" />
            <p className="text-sm text-slate-600">
              Her paket hangi sayfalara erişilebileceğini ve fiyatını belirler. Ödeme entegrasyonu eklendiğinde öğretmenler bu fiyatlar üzerinden satın alma yapabilecek.
            </p>
          </div>
        </Card>

        {packages.length === 0 ? (
          <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500">
            <Package className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            Henüz paket yok. &quot;Yeni Paket&quot; ile başlayın.
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {packages.map((pkg) => (
              <Card key={pkg.id} className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-heading text-lg font-bold text-slate-900">{pkg.name}</h3>
                    {pkg.description && <p className="mt-1 text-sm text-slate-500">{pkg.description}</p>}
                    <p className="mt-2 flex items-center gap-1.5 font-heading text-xl font-bold text-violet-600">
                      <Banknote className="h-5 w-5" />
                      {formatPackagePrice(pkg)}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-400">
                      {BILLING_PERIOD_LABELS[pkg.billing_period] || pkg.billing_period} · {pkg.currency}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(pkg)} className="h-8 w-8 rounded-lg"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(pkg)} className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(pkg.permissions || []).map((key) => {
                    const page = PAGE_PERMISSIONS.find((p) => p.key === key);
                    return (
                      <Badge key={key} className="rounded-full bg-violet-100 text-violet-700 hover:bg-violet-100">
                        {page?.label || key}
                      </Badge>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Paket Düzenle" : "Yeni Paket"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Paket Adı *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" placeholder="Örn. Premium Paket" />
            </div>
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" placeholder="Kısa açıklama (opsiyonel)" />
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
              <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Banknote className="h-4 w-4 text-violet-600" /> Fiyat Bilgisi
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="space-y-2 sm:col-span-1">
                  <Label>Fiyat *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="rounded-xl"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Para Birimi</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRY">TRY (₺)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Faturalama</Label>
                  <Select value={form.billing_period} onValueChange={(v) => setForm({ ...form, billing_period: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Aylık</SelectItem>
                      <SelectItem value="yearly">Yıllık</SelectItem>
                      <SelectItem value="one_time">Tek seferlik</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Erişim Verilen Sayfalar</Label>
              <div className="max-h-64 space-y-2 overflow-y-auto rounded-xl border border-slate-200 p-3">
                {PAGE_PERMISSIONS.map((page) => (
                  <div key={page.key} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                    <span className="text-sm font-medium text-slate-700">{page.label}</span>
                    <Switch checked={form.permissions.includes(page.key)} onCheckedChange={() => togglePerm(page.key)} />
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={saving} className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Kaydet</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Paketi sil?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.name}</strong> silinecek. Bu pakete atanmış öğretmenlerin paket ataması kaldırılır.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-xl bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
